import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLoginMutation } from "../use-login-mutation";
import { removeLocalStorageItem, setLocalStorageItem } from "../../utils";
import { useLocation, useNavigate } from "react-router";
import { Views } from "../../utils/types";
import { useRefreshTokenMutation } from "../use-refresh-token-mutation";
import { useScreenAlert } from "../use-screen-alert";
import { getUserSessionInfo, USER_SESSION_KEY, normalizeCapabilities } from "./utils";
import { UserContextValue, User, UserCapabilities, UserRoles } from "./types";
import { useMultiSiteContext } from "../use-site-context";
import { clearAggregateAssetsCache } from "@hooks/use-get/aggregate-assets";

const UserContext = createContext<UserContextValue>({
  user: null,
  login: () => Promise.resolve({} as User),
  logout: () => { },
  checkUserAuthentication: () => Promise.resolve(undefined),
});

export const UserContextProvider = ({ children }: PropsWithChildren) => {
  const [user, setUser] = useState<User | null>(null);
  const [capabilities, setCapabilities] = useState<UserCapabilities>({});

  const { setError, setSuccess } = useScreenAlert();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { getApiUrl } = useMultiSiteContext();

  const fetchMe = useCallback(
    async (authToken: string) => {
      try {
        const res = await fetch(getApiUrl("wp-json/smassets/v1/me"), {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        const caps = normalizeCapabilities(data?.capabilities);
        setCapabilities(caps);
        setUser((prev) =>
          prev ? { ...prev, capabilities: caps } : prev
        );
        const stored = getUserSessionInfo(null).storedSession;
        if (stored) {
          setLocalStorageItem(USER_SESSION_KEY, {
            ...stored,
            user: { ...(stored.user ?? {}), capabilities: caps },
          });
        }
      } catch {
        // non-critical
      }
    },
    [getApiUrl],
  );
  const prevPath = useRef<string | null>(null);
  const { loginWithPassword, loading } = useLoginMutation();
  const { refreshUserToken, loading: refreshLoading } =
    useRefreshTokenMutation();

  // Read via ref (not `user` state) inside stable callbacks below — this keeps
  // `refreshUserSession`/`checkUserAuthentication`'s identity from changing on
  // every token refresh, which otherwise re-triggers every effect/hook that
  // lists them as a dependency (e.g. table data fetchers) across the app.
  const userRef = useRef<User | null>(user);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Same rationale as userRef, but for `pathname` — without it, every route
  // navigation would change `refreshUserSession`/`checkUserAuthentication`'s
  // identity (they only read pathname to decide where to navigate).
  const pathnameRef = useRef(pathname);
  useEffect(() => {
    pathnameRef.current = pathname;
  }, [pathname]);

  useEffect(() => {
    if (prevPath.current !== pathname && pathname !== Views.SIGN_IN && pathname !== Views.HOME) {
      prevPath.current = pathname;
    }
  }, [pathname]);

  const login = useCallback(async (email: string, password: string) => {
    try {
      const result = await loginWithPassword(email, password);
      const {
        authToken,
        refreshToken,
        user,
        refreshTokenExpiration,
        authTokenExpiration,
      } = result;
      setLocalStorageItem(USER_SESSION_KEY, result);
      const caps = normalizeCapabilities(user?.capabilities);
      setUser(
        !user
          ? null
          : {
            ...(user ?? {}),
            authToken,
            refreshToken,
            refreshTokenExpiration,
            authTokenExpiration,
            role: user?.roles?.nodes?.[0]?.name || UserRoles.Student,
            capabilities: caps,
          }
      );
      setCapabilities(caps);
      setSuccess("Login successful!");
      fetchMe(authToken);
      return user;
    } catch (err) {
      setError("Login failed. Please try again.");
      return;
    }
  }, [loginWithPassword, setSuccess, setError, fetchMe]);

  const logout = useCallback(() => {
    removeLocalStorageItem(USER_SESSION_KEY);
    clearAggregateAssetsCache();
    setUser(null);
    setCapabilities({});
    navigate(Views.SIGN_IN);
  }, [navigate]);

  const refreshUserSession = useCallback(async ({
    silent = false,
  }: { silent?: boolean } = {}) => {
    const { userSession } = getUserSessionInfo(userRef.current);

    if (!userSession) return;

    const result = await refreshUserToken(userSession?.refreshToken);
    if (result) {
      const { authToken, authTokenExpiration } = result;
      const updatedSession = {
        ...userSession,
        authToken,
        authTokenExpiration,
      };
      setLocalStorageItem(USER_SESSION_KEY, updatedSession);
      const restoredCaps = normalizeCapabilities(
        userSession?.capabilities ?? userSession?.user?.capabilities
      );

      // Always update the token in React state so the proactive-refresh timer
      // effect re-runs with the new expiration and reschedules itself.
      // For silent (proactive) refreshes, skip fetchMe — that extra network
      // round-trip is only needed when the user's capabilities may have changed.
      setUser({
        ...updatedSession,
        firstName: userSession.firstName ?? userSession.user?.firstName,
        lastName: userSession.lastName ?? userSession.user?.lastName,
        email: userSession.email ?? userSession.user?.email,
        role:
          userSession.role ??
          userSession.user?.roles?.nodes?.[0]?.name ??
          userSession.user?.role,
        capabilities: restoredCaps,
      });
      setCapabilities(restoredCaps);

      if (!silent) {
        // Re-fetch fresh capabilities from the server now that we have a new
        // auth token — prevents stale localStorage caps from persisting after
        // a token refresh.
        fetchMe(authToken);
      }

      if (pathnameRef.current === Views.SIGN_IN) {
        navigate(prevPath.current ?? Views.ASSET_TABLE);
      }
      return true;
    } else {
      removeLocalStorageItem(USER_SESSION_KEY);
      navigate(Views.SIGN_IN);
    }
  }, [refreshUserToken, navigate, fetchMe]);

  const checkUserAuthentication = useCallback(async () => {
    const { isSessionValid, isRefreshTokenValid } = getUserSessionInfo(userRef.current);

    if (!isSessionValid && isRefreshTokenValid) {
      return await refreshUserSession();
    } else if (!isSessionValid && !isRefreshTokenValid) {
      removeLocalStorageItem(USER_SESSION_KEY);
      if (pathnameRef.current !== Views.SIGN_IN) {
        navigate(Views.SIGN_IN);
      }
      return;
    }
  }, [refreshUserSession, navigate]);

  // Check user authentication on initial load/page refresh
  useEffect(() => {
    const { storedSession, sessionExpirationInMS, currentTime } =
      getUserSessionInfo(user);

    const ensureAuthenticated = async () => {
      const result = await checkUserAuthentication();
      if (!result && pathname !== Views.SIGN_IN) {
        removeLocalStorageItem(USER_SESSION_KEY);
        navigate(Views.SIGN_IN);
      }
    };

    if (!user) {
      if (storedSession) {
        const session = storedSession;

        if (currentTime > sessionExpirationInMS) {
          ensureAuthenticated();
        } else {
          const restoredCaps = normalizeCapabilities(session.user?.capabilities);
          setUser({
            ...(session.user ?? {}),
            authToken: session.authToken,
            refreshToken: session.refreshToken,
            authTokenExpiration: session.authTokenExpiration,
            refreshTokenExpiration: session.refreshTokenExpiration,
            role:
              session.role ??
              session.user?.role ??
              session?.user?.roles?.nodes?.[0]?.name,
            capabilities: restoredCaps,
          });
          setCapabilities(restoredCaps);
          fetchMe(session.authToken);
          if (pathname !== Views.RESET_PASSWORD) {
            navigate(prevPath.current ?? Views.ASSET_TABLE);
          }
        }
      } else {
        if (pathname !== Views.RESET_PASSWORD && pathname !== Views.SIGN_IN) {
          navigate(Views.SIGN_IN);
        }
      }
    } else {
      if (pathname === Views.SIGN_IN) {
        navigate(
          prevPath.current && prevPath.current !== Views.RESET_PASSWORD
            ? prevPath.current
            : Views.ASSET_TABLE
        );
      }
    }
  }, [user, navigate, pathname, checkUserAuthentication, fetchMe]);

  // Check session validity when user returns to the tab (handles 401 after navigating away)
  useEffect(() => {
    const handleFocus = async () => {
      const { isSessionValid, isRefreshTokenValid } = getUserSessionInfo(user);

      if (!isSessionValid && isRefreshTokenValid) {
        await refreshUserSession({ silent: true });
      } else if (!isSessionValid && !isRefreshTokenValid) {
        removeLocalStorageItem(USER_SESSION_KEY);
        setUser(null);
        setCapabilities({});
        navigate(Views.SIGN_IN);
      }
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
  }, [user, refreshUserSession, navigate]);

  // Proactively refresh the authToken 60 seconds before it expires so the
  // user is never holding a stale token while idle on a page.
  // Reads via userRef (not `user` state) so this only reschedules when the
  // expiration itself changes, not on every unrelated user/capabilities update.
  useEffect(() => {
    if (!userRef.current) return;

    const { sessionExpirationInMS, currentTime, isRefreshTokenValid } =
      getUserSessionInfo(userRef.current);

    const msUntilExpiry = sessionExpirationInMS - currentTime;
    const msUntilRefresh = msUntilExpiry - 60_000; // fire 60s before expiry

    if (msUntilRefresh <= 0) {
      // Already expired or within the 60s window — refresh immediately if possible
      if (isRefreshTokenValid) {
        refreshUserSession({ silent: true });
      }
      return;
    }

    const timer = setTimeout(() => {
      const { isRefreshTokenValid: stillValid } = getUserSessionInfo(userRef.current);
      if (stillValid) {
        refreshUserSession({ silent: true });
      }
    }, msUntilRefresh);

    return () => clearTimeout(timer);
  }, [user?.authTokenExpiration, refreshUserSession]);

  const contextValue = useMemo(
    () => ({
      user,
      login,
      logout,
      checkUserAuthentication,
      loading: loading || refreshLoading,
      capabilities,
    }),
    [user, login, logout, checkUserAuthentication, loading, refreshLoading, capabilities]
  );

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};

export const useUserContext = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUserContext must be used within a UserContextProvider");
  }
  return context;
};
